/** Instance-owned rewards extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamRewards(runtime) {
  runtime.processQueuedEffects = function (now = runtime.now()) {
    let write = 0;
    for (let read = 0; read < runtime.game.effectQueue.length; read += 1) {
      const item = runtime.game.effectQueue[read];
      if (!item || item.at > now) {
        runtime.game.effectQueue[write] = item;
        write += 1;
        continue;
      }
      item.effect();
    }
    runtime.game.effectQueue.length = write;
  };
  runtime.addBallDestroyRewards = function (ball, brick, x, y, baseCash) {
    const comboBonus = runtime.getBallComboBonus(ball);
    const baseScore = brick.maxHp * 8;
    ball.contributionScore =
      Math.max(0, Math.floor(ball.contributionScore || 0)) + baseScore + comboBonus;
    runtime.addScore(
      baseScore + comboBonus,
      x,
      y,
      comboBonus > 0 ? `+${runtime.formatBigNumber(baseScore + comboBonus)}` : null,
    );
    const cashAward = Math.max(0, Math.floor(baseCash + comboBonus));
    runtime.addCoins(
      cashAward,
      x,
      y + 12,
      comboBonus > 0 ? `+$${runtime.formatBigNumber(cashAward)} COMBO` : null,
    );
    ball.combo = Math.max(0, Math.floor(ball.combo || 0)) + 1;
    ball.comboScoreBonus = comboBonus + 1;
    if (ball.combo > 0) {
      const scale = runtime.clamp(1 + ball.combo * 0.18, 1.2, 1.54);
      const life = runtime.clamp(0.75 + ball.combo * 0.08, 0.95, 1.75);
      runtime.addFloater(x, y - 16, `COMBO x${ball.combo} +${comboBonus + 1}`, '#ff4fd8', {
        scale,
        life,
        stroke: 'rgba(42, 6, 70, 0.88)',
        shadowBlur: 18 + ball.combo * 4,
      });
    }
  };
  runtime.hasLiveBricks = function () {
    return runtime.game.bricks.some((brick) => !brick.dead && brick.y < runtime.H);
  };
  runtime.finishClassicBoardClearVolley = function () {
    if (runtime.game.mode !== 'classic') return;
    for (const ball of runtime.game.balls) {
      ball.dead = true;
    }
    runtime.game.launchQueue = 0;
    runtime.compactBalls();
    runtime.game.volleyActive = false;
    runtime.game.status = 'ready';
    runtime.game.volleyStartedAt = 0;
    runtime.game.roundAdvancedForVolley = false;
    runtime.game.mods.unshift('Classic board clear: next launch ready');
    runtime.game.mods.length = 4;
  };
  runtime.refillBoardAfterClear = function () {
    runtime.game.boardClearRefillRows = Math.max(
      runtime.BOARD_CLEAR_REFILL_ROWS,
      Math.floor(runtime.game.boardClearRefillRows || 0),
    );
    const spawned = runtime.advanceRoundAfterVolley({ force: true });
    if (spawned > 0 && runtime.game.status !== 'gameover') {
      runtime.game.mods.unshift(
        `Board clear refill: ${runtime.BOARD_CLEAR_REFILL_ROWS} fresh rows`,
      );
      runtime.game.mods.length = 4;
    }
    return spawned;
  };
  runtime.checkBoardClearReward = function (x = runtime.W / 2, y = runtime.H / 2) {
    if (runtime.game.boardClearRewarded || runtime.hasLiveBricks()) return;
    runtime.game.boardClearRewarded = true;
    runtime.game.boardClears += 1;
    const levelValue = Math.max(1, Math.floor(runtime.game.level));
    const hitPowerGain = Math.max(
      1,
      Math.round(levelValue * runtime.BOARD_CLEAR_HIT_POWER_LEVEL_SHARE),
    );
    runtime.game.boardClearHitPower = runtime.getBoardClearHitPowerBonus() + hitPowerGain;
    const reward = runtime.BOARD_CLEAR_CASH_PER_LEVEL * levelValue;
    runtime.addExactCoins(
      reward,
      runtime.W / 2,
      runtime.H * 0.35,
      `BOARD CLEAR +$${runtime.formatGroupedNumber(reward)}`,
    );
    runtime.addFloater(runtime.W / 2, runtime.H * 0.27, 'BOARD CLEAR', '#fff4c2', {
      scale: 2.9,
      life: 2.6,
      vy: -18,
      stroke: 'rgba(84, 26, 4, 0.95)',
      shadowBlur: 48,
    });
    runtime.addFloater(
      runtime.W / 2,
      runtime.H * 0.43,
      `HIT POWER +${runtime.formatGroupedNumber(hitPowerGain)}`,
      '#74ff8d',
      {
        scale: 1.8,
        life: 2.2,
        vy: -24,
        stroke: 'rgba(20, 65, 16, 0.9)',
        shadowBlur: 34,
      },
    );
    runtime.game.mods.unshift(
      `Board clear bonus: $${runtime.formatGroupedNumber(reward)}; hit power +${runtime.formatGroupedNumber(hitPowerGain)} (${runtime.formatGroupedNumber(runtime.getCurrentPlayerBallDamage())} damage)`,
    );
    runtime.game.mods.length = 4;
    runtime.game.screenFlash = Math.max(runtime.game.screenFlash, 1);
    runtime.game.shake = Math.max(runtime.game.shake, 0.9);
    runtime.explode(x, y, '#ffd166', 90, 1.6);
    runtime.shockwave(runtime.W / 2, runtime.H * 0.36, '#74ff8d');
    runtime.addBurst(runtime.W / 2, runtime.H * 0.36, '#fff4c2', 42, 330, 0.92, 7);
    runtime.spawnMoneyRain(reward);
    runtime.finishClassicBoardClearVolley();
    runtime.refillBoardAfterClear();
    runtime.accelerateClassicChain();
    runtime.syncHud();
  };
  runtime.recordBrickDestroyed = function (brick) {
    if (!brick || brick.destroyCounted) return;
    brick.destroyCounted = true;
    if (runtime.game.mode !== 'speed') return;
    runtime.game.destroyedBlocksSinceAdvance += 1;
  };
  runtime.addSpeedPressureRow = function (queued = false, source = 'blocks') {
    runtime.game.roundAdvancedForVolley = false;
    const spawned = runtime.advanceRoundAfterVolley({ force: true });
    runtime.game.speedNextAdvanceBlocks = Math.max(
      1,
      spawned || runtime.SPEED_INITIAL_ADVANCE_BLOCKS,
    );
    if (runtime.game.status === 'gameover') return;
    runtime.game.mods.unshift(
      queued
        ? `Speed catch-up row; next in ${runtime.game.speedNextAdvanceBlocks} blocks`
        : source === 'timer'
          ? `Speed timer row; next timed row in ${runtime.SPEED_TIMED_PRESSURE_SECONDS}s`
          : `Speed pressure: next row in ${runtime.game.speedNextAdvanceBlocks} blocks`,
    );
    runtime.game.mods.length = 4;
  };
  runtime.processSpeedBlockAdvances = function (dt = 0) {
    if (runtime.game.mode !== 'speed') return;
    if (runtime.game.speedQueuedRows > 0 && runtime.game.status !== 'gameover') {
      runtime.game.speedQueuedRowTimer += dt;
      if (runtime.game.speedQueuedRowTimer >= runtime.speedRowInterval()) {
        runtime.game.speedQueuedRowTimer -= runtime.speedRowInterval();
        runtime.game.speedQueuedRows -= 1;
        runtime.addSpeedPressureRow(true);
      }
    }
    let advancedImmediate = false;
    while (
      runtime.game.status !== 'gameover' &&
      runtime.game.destroyedBlocksSinceAdvance >= runtime.game.speedNextAdvanceBlocks
    ) {
      runtime.game.destroyedBlocksSinceAdvance -= runtime.game.speedNextAdvanceBlocks;
      if (!advancedImmediate && runtime.game.speedQueuedRows === 0) {
        runtime.addSpeedPressureRow(false);
        advancedImmediate = true;
        continue;
      }
      if (runtime.game.speedQueuedRows < runtime.SPEED_MAX_QUEUED_ROWS) {
        runtime.game.speedQueuedRows += 1;
        runtime.game.speedQueuedRowTimer = Math.min(
          runtime.game.speedQueuedRowTimer,
          runtime.SPEED_QUEUED_ROW_INTERVAL_SECONDS * 0.35,
        );
        runtime.game.mods.unshift(
          `Speed catch-up: ${runtime.game.speedQueuedRows} row${runtime.game.speedQueuedRows === 1 ? '' : 's'} queued`,
        );
        runtime.game.mods.length = 4;
        continue;
      }
      runtime.game.destroyedBlocksSinceAdvance = 0;
      break;
    }
  };
  runtime.destroyBrick = function (brick) {
    if (!brick || brick.dead) return false;
    brick.dead = true;
    runtime.game.spatialDirty = true;
    runtime.markBoardSceneDirty();
    runtime.recordBrickDestroyed(brick);
    return true;
  };
  runtime.addBurst = function (x, y, color, radius = 18, speed = 120, life = 0.42, width = 3) {
    const maxBursts =
      runtime.game.energyMode === 'pressureGuard'
        ? Math.max(16, Math.floor(runtime.MAX_BURSTS * 0.5))
        : runtime.MAX_BURSTS;
    runtime.game.bursts.push({
      x,
      y,
      color,
      radius,
      speed,
      life,
      maxLife: life,
      width,
    });
    if (runtime.game.bursts.length > maxBursts)
      runtime.game.bursts.splice(0, runtime.game.bursts.length - maxBursts);
  };
  runtime.addImpactFlash = function (x, y, color = '#ffd166', intensity = 1) {
    runtime.spark(x, y, color, Math.floor(8 + intensity * 10));
    runtime.addBurst(
      x,
      y,
      color,
      8 + intensity * 4,
      80 + intensity * 70,
      0.22 + intensity * 0.08,
      1.5 + intensity,
    );
  };
  runtime.collideBricks = function (ball) {
    const candidates = runtime.queryPointBuckets(runtime.game.brickBuckets, ball.x, ball.y);
    const queryMark = runtime.game.queryMark;
    runtime.game.queryMark += 1;
    for (let i = 0; i < candidates.length; i += 1) {
      const brick = candidates[i];
      if (brick.dead || brick.queryMark === queryMark) continue;
      brick.queryMark = queryMark;
      const closestX = runtime.clamp(ball.x, brick.x, brick.x + brick.w);
      const closestY = runtime.clamp(ball.y, brick.y, brick.y + brick.h);
      const dx = ball.x - closestX;
      const dy = ball.y - closestY;
      if (dx * dx + dy * dy > ball.r * ball.r) continue;
      const overlapX = Math.min(Math.abs(ball.x - brick.x), Math.abs(ball.x - (brick.x + brick.w)));
      const overlapY = Math.min(Math.abs(ball.y - brick.y), Math.abs(ball.y - (brick.y + brick.h)));
      if (overlapX < overlapY) ball.vx *= -1;
      else ball.vy *= -1;
      ball.sideBounceStreak = 0;
      ball.wallBounceStreak = 0;
      const damage = runtime.getBrickHitDamage(ball);
      brick.hp -= damage;
      brick.heat = 1;
      runtime.markBoardSceneDirty();
      const comboBonus = runtime.getBallComboBonus(ball);
      const hitScore = 6 * damage + comboBonus;
      ball.contributionScore = Math.max(0, Math.floor(ball.contributionScore || 0)) + hitScore;
      runtime.addScore(hitScore, ball.x, ball.y, runtime.formatBrickHitDamageLabel(damage));
      if (runtime.random() < 0.08 + Math.min(0.12, runtime.game.crit * 0.02)) {
        runtime.addCoins(1, ball.x, ball.y + 10, '+$1 hit');
      }
      runtime.addImpactFlash(
        ball.x,
        ball.y,
        ball.neutron ? '#fff8f0' : runtime.brickColor(brick),
        ball.neutron ? 1.7 : 0.7,
      );
      runtime.spendTemporaryHit(ball);
      if (ball.dead) return;
      if (brick.hp <= 0) {
        runtime.destroyBrick(brick);
        runtime.addBallDestroyRewards(
          ball,
          brick,
          brick.x + brick.w / 2,
          brick.y + brick.h / 2,
          1 + Math.floor(brick.maxHp / 18) + Math.floor(runtime.game.crit / 3),
        );
        runtime.checkBoardClearReward(brick.x + brick.w / 2, brick.y + brick.h / 2);
        runtime.explode(brick.x + brick.w / 2, brick.y + brick.h / 2, '#ff8a3d', 28, 0.72);
        runtime.addBurst(
          brick.x + brick.w / 2,
          brick.y + brick.h / 2,
          '#ffd166',
          10,
          150,
          0.34,
          2.5,
        );
      }
      if (ball.pierce > 0) {
        ball.pierce -= 1;
      } else {
        return;
      }
    }
  };
}
